import { gql, useLazyQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { AccountStates } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { Search } from '@mui/icons-material';
import type { SxProps } from '@mui/material';
import {
  InputAdornment,
  TextField,
} from '@ovComponents/2-component/textField/textField';
import { UserContext } from '@providers/userContextProvider';
import { uniqBy } from 'lodash-es';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';
import { ACCOUNT_STATES } from '../accountSelect/accountSelect';

const MAX_SEARCH_RESULTS = 12;

const SEARCH_ACCOUNTS = ({
  canReadAccountNumber,
}: {
  canReadAccountNumber: boolean;
}) => gql`
  query searchAccounts($input: AccountSearchInput!, $currency: StatisticsCurrencyTypes) {
    searchAccounts(input: $input) {
      totalCount
      accounts {
        id
        type
        state
        translatedNickName { ${TRANSLATED_STRING_FIELDS} }
        user { entityName firstName lastName }
        ${canReadAccountNumber ? 'custodianAccountNumber' : ''}
        statistics (input: { currency: $currency }) { marketValueCents }
      }
    }
  }
`;

type AccountSearchProps = {
  label?: string;
  placeholder?: string;
  setAccounts: (accounts: Account[]) => void;
  setHasMore: (hasMore: boolean) => void;
  sx?: SxProps;
  setLoadMoreHandler?: (loadMore: () => void) => void;
  objectType?: 'USER' | 'HOUSEHOLD' | 'ORGANIZATION';
  objectId?: string;
  page: number;
  setPage: (page: number) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  isUngrouped?: boolean;
  accountGroupId?: string;
  excludeAccountIds?: string[];
  setLoading?: (loading: boolean) => void;
  setIsSearching?: (isSearching: boolean) => void;
  setInitialLoading?: (initialLoading: boolean) => void;
};

export const AccountSearch = ({
  label,
  placeholder,
  setAccounts,
  setHasMore,
  sx,
  setLoadMoreHandler,
  objectType,
  objectId,
  page,
  setPage,
  searchText,
  setSearchText,
  isUngrouped = false,
  accountGroupId,
  excludeAccountIds = [],
  setLoading,
  setIsSearching,
  setInitialLoading,
}: AccountSearchProps) => {
  const { language } = useLocalization();
  const { activeCurrency } = useContext(UserContext);
  const canReadAccountNumber = usePermission('read:account_number');
  const [accumulatedAccounts, setAccumulatedAccounts] = useState<Account[]>([]);
  const [excludeAccountIdsCount, setExcludeAccountIdsCount] =
    useState<number>(0);

  const filter = {
    ...(accountGroupId ? { accountGroupId } : {}),
    ...(objectType === 'HOUSEHOLD'
      ? { clientGroupId: objectId }
      : objectType === 'ORGANIZATION'
        ? { organizationId: objectId }
        : { userId: objectId }),
    ...(isUngrouped ? { isUngrouped } : {}),
    language: language,
    excludeAccountIds,
    restrictToOrganizationId: true,
    states: [...ACCOUNT_STATES, AccountStates.Canceled, AccountStates.Inactive],
  };

  const [searchAccounts] = useLazyQuery(
    SEARCH_ACCOUNTS({ canReadAccountNumber }),
    {
      fetchPolicy: 'no-cache',
    },
  );

  const handleSearchAccountsResponse = useCallback(
    (
      results: Array<{
        data?: {
          searchAccounts?: { accounts?: Account[]; totalCount?: number };
        };
      }>,
      newPage: number,
      previousAccounts: Account[],
    ) => {
      const allAccounts = results.flatMap(
        (result) => result.data?.searchAccounts?.accounts || [],
      );
      const mergedAccounts = uniqBy(
        [
          ...(excludeAccountIds.length > 0
            ? previousAccounts.filter(
                (acc) => !excludeAccountIds.includes(acc.id),
              )
            : previousAccounts),
          ...allAccounts,
        ],
        'id',
      );
      const lastResult = results.at(-1);
      const totalCount = lastResult?.data?.searchAccounts?.totalCount || 0;

      setAccumulatedAccounts(mergedAccounts);
      setAccounts(mergedAccounts);
      setExcludeAccountIdsCount(excludeAccountIds.length);
      setHasMore(newPage * MAX_SEARCH_RESULTS < totalCount);
      setLoading?.(false);
      setInitialLoading?.(false);
    },
    [
      excludeAccountIds,
      setAccounts,
      setHasMore,
      setLoading,
      setIsSearching,
      setInitialLoading,
    ],
  );

  const loadMoreAccounts = useCallback(async () => {
    let newPage = page + 1;
    const pagesToFetch: number[] = [];

    if (
      excludeAccountIds.length > 0 &&
      excludeAccountIds.length !== excludeAccountIdsCount
    ) {
      if (excludeAccountIds.length >= MAX_SEARCH_RESULTS) {
        const calculatedPages = excludeAccountIds.length / MAX_SEARCH_RESULTS;
        const startPage = Math.max(1, Math.ceil(page - calculatedPages));
        newPage = page;
        for (let p = startPage; p <= page; p++) {
          pagesToFetch.push(p);
        }
      } else {
        pagesToFetch.push(page, newPage);
      }
    } else {
      pagesToFetch.push(newPage);
    }

    setPage(newPage);
    setLoading?.(true);

    try {
      const results = await Promise.all(
        pagesToFetch.map((pageNum) =>
          searchAccounts({
            variables: {
              input: {
                filter: {
                  query: searchText,
                  ...filter,
                },
                pagination: { perPage: MAX_SEARCH_RESULTS, page: pageNum },
              },
              currency: activeCurrency || 'CAD',
            },
          }),
        ),
      );

      handleSearchAccountsResponse(results, newPage, accumulatedAccounts);
    } catch {
      setIsSearching?.(false);
      setLoading?.(false);
      setInitialLoading?.(false);
    }
  }, [
    page,
    excludeAccountIds,
    excludeAccountIdsCount,
    searchText,
    searchAccounts,
    filter,
    handleSearchAccountsResponse,
    accumulatedAccounts,
    activeCurrency,
    setPage,
    setLoading,
    setIsSearching,
    setInitialLoading,
  ]);

  const performSearch = useCallback(
    async (query: string) => {
      setAccumulatedAccounts([]);
      setIsSearching?.(true);
      setLoading?.(true);
      setPage(1);

      try {
        const result = await new Promise<{
          data?: {
            searchAccounts?: { accounts?: Account[]; totalCount?: number };
          };
        }>((resolve, reject) => {
          searchAccounts({
            variables: {
              input: {
                filter: {
                  query,
                  ...filter,
                },
                pagination: { perPage: MAX_SEARCH_RESULTS, page: 1 },
              },
              currency: activeCurrency || 'CAD',
            },
            fetchPolicy: 'no-cache',
            onCompleted: (data) => resolve({ data }),
            onError: (err) => reject(err),
          });
        });

        handleSearchAccountsResponse([result], 1, []);
        if (query.length === 0) setIsSearching?.(false);
      } catch {
        setIsSearching?.(false);
        setLoading?.(false);
        setInitialLoading?.(false);
      }
    },
    [
      searchAccounts,
      filter,
      handleSearchAccountsResponse,
      setPage,
      setLoading,
      setIsSearching,
      setInitialLoading,
    ],
  );

  useEffect(() => {
    setLoadMoreHandler?.(loadMoreAccounts);
  }, [setLoadMoreHandler]);

  useEffect(() => {
    setInitialLoading?.(true);
    performSearch('');
  }, []);

  return (
    <TextField
      fullWidth
      label={label ?? ''}
      value={searchText}
      placeholder={placeholder ?? ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        setPage(1);
        performSearch(value);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <Search />
          </InputAdornment>
        ),
      }}
      sx={sx}
    />
  );
};
